
import React, { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import PageLayout from "@/components/layout/PageLayout";
import { cn } from "@/lib/utils";

interface MeetingData {
  Id: string;
  TillaegsDagsorden: boolean;
  Udvalg: {
    Navn: string;
  };
  Moede: {
    Id: string;
    Dato: string;
    Afsluttet: boolean;
  };
}

const ReferatTest = () => {
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MeetingData[]>([]);
  const { toast } = useToast();

  const fetchData = async () => {
    if (!startDate || !endDate) {
      toast({
        title: "Fejl",
        description: "Vælg venligst både start- og slutdato.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Format dates as required by the API (YYYY-MM-DD)
      const formattedStartDate = format(startDate, "yyyy-MM-dd");
      const formattedEndDate = format(endDate, "yyyy-MM-dd");

      // Prepare the request payload
      const requestPayload = {
        select: {
          Id: "true",
          TillaegsDagsorden: "true",
          Udvalg: {
            Navn: "true"
          },
          Moede: {
            Id: "true",
            Dato: "true",
            Afsluttet: "true"
          }
        },
        kriterie: {
          udvalgId: "ce683df4-0beb-473c-abb1-b81860a31028",
          moedeDato: [formattedStartDate, formattedEndDate]
        },
        paging: {
          startIndex: 0,
          limit: 20
        }
      };

      // Use a proxy to handle potential CORS issues
      const proxyUrl = "https://cors-anywhere.herokuapp.com/";
      const apiUrl = "https://dagsordener.htk.dk/api/agenda/soeg/";
      
      const response = await fetch(`${apiUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        throw new Error(`API response error: ${response.status}`);
      }

      const responseData = await response.json();
      setData(responseData.records || []);
      
      toast({
        title: "Success",
        description: `Hentede ${responseData.records?.length || 0} poster.`,
      });
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("På grund af CORS-begrænsninger kunne dataene ikke hentes. Prøv igen senere.");
      
      toast({
        title: "Fejl ved hentning af data",
        description: "Det er muligt, at dette er et CORS-problem. Se mere information nedenfor.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayout
      title="Referat test"
      description="Test af dataudtræk fra dagsordener"
      icon={<CalendarIcon className="h-6 w-6" />}
    >
      <div className="container mx-auto py-8 space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Vælg datointerval</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Fra dato:</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "dd/MM/yyyy") : <span>Vælg startdato</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Til dato:</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "dd/MM/yyyy") : <span>Vælg slutdato</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="flex items-end">
                <Button 
                  onClick={fetchData} 
                  disabled={isLoading || !startDate || !endDate}
                  className="min-w-20"
                >
                  {isLoading ? "Henter..." : "Søg"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-6">
              <p className="text-red-600">{error}</p>
              <p className="mt-2 text-sm">
                Dette er sandsynligvis på grund af CORS-begrænsninger. Prøv at implementere en proxy server eller kontakt API-ejeren for at få tilladelse.
              </p>
            </CardContent>
          </Card>
        )}

        {data.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Resultater</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Tillægsdagsorden</TableHead>
                      <TableHead>Udvalg</TableHead>
                      <TableHead>Møde ID</TableHead>
                      <TableHead>Møde Dato</TableHead>
                      <TableHead>Afsluttet</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.map((item) => (
                      <TableRow key={item.Id}>
                        <TableCell>{item.Id}</TableCell>
                        <TableCell>{item.TillaegsDagsorden ? "Ja" : "Nej"}</TableCell>
                        <TableCell>{item.Udvalg?.Navn || "N/A"}</TableCell>
                        <TableCell>{item.Moede?.Id || "N/A"}</TableCell>
                        <TableCell>
                          {item.Moede?.Dato 
                            ? new Date(item.Moede.Dato).toLocaleDateString('da-DK') 
                            : "N/A"}
                        </TableCell>
                        <TableCell>{item.Moede?.Afsluttet ? "Ja" : "Nej"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
};

export default ReferatTest;
